// Phase 45.5 — useFactoryPacketExtract (INGEST-01, D-04 / D-17).
//
// The trigger hook for the "Confirm & extract →" hand-off. CombinedUploadReview
// calls trigger() on confirm; LiveFillCanvas reads `jobId` to subscribe the
// streaming progressive fill (useFactoryPacketFill) and the Copilot reading
// trail (useAgentSteps), and `isReading` to render the "now reading your
// documents…" transition state.
//
// Fans out the Laravel FactoryPacketOrchestrator (router → 3 extractors in
// parallel → gap-filler last). Fire-and-forget: the 202 ACK returns immediately;
// the progressive fill + per-document done-state arrive over Centrifugo.
//
// The orchestrator dispatches multiple per-document jobs, each on its own
// ai-stream channel. For the canvas we key the stream subscription on the
// inventory item id (the orchestrator broadcasts the packet stream on a
// per-item channel) — exposed as `jobId` so the canvas's existing
// useAgentSteps / ai-stream consumers wire unchanged.

import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { aiApi } from '@/modules/inventory/services/aiApi';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import { AI_PERMISSION_DENIED_STATUS } from '@/modules/inventory/hooks/paidAiGuard';

interface FactoryPacketExtractState {
  /** Fire the extract for the given saved boat (inventory item) id. */
  trigger: (boatId: string) => void;
  /**
   * The stream key the canvas subscribes to for progressive fill + the Copilot
   * reading trail. The orchestrator broadcasts the packet stream on the boat's
   * item-scoped channel, so this is the boat id once extraction is triggered.
   */
  jobId: string | null;
  /** True from confirm until the first fields land — drives "now reading…". */
  isReading: boolean;
  /**
   * CR-03: clear the optimistic `isReading` state on extraction completion.
   * `isReading` is set true on trigger()/dispatch and (prior to this) was only
   * ever cleared onError — so a SUCCESSFUL dispatch left the hero frozen on
   * "now reading your documents…" for the rest of the session, hiding the
   * ingest cards. LiveFillCanvas wires this to useFactoryPacketFill's terminal
   * `done` callback so it fires on both the streaming-partial path AND the
   * terminal-done (refetch) path (streaming is currently deferred).
   */
  markReadingComplete: () => void;
  /** The most recent dispatch status (for fail-soft messaging). */
  status: string | null;
}

export function useFactoryPacketExtract(): FactoryPacketExtractState {
  // ACL-09 (#611, D-22, T-09-06e) — the extract is paid AI. Without
  // inventory.ai.generate the hand-off settles straight into a "no fill" state
  // and sends nothing. #1458 (WR-04): that state is skipped_no_permission, not
  // the server's skipped_ai_disabled (AI off for the dealership).
  const canGenerate = useCan('inventory.ai.generate');
  const [jobId, setJobId] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (boatId: string) => aiApi.extractFactoryPacket(boatId),
    onSuccess: (res) => {
      setStatus(res.status);
      // Only enter the "reading" state when the orchestrator actually
      // dispatched (a fail-soft skip means no fill is coming).
      if (res.status === 'dispatched') {
        setIsReading(true);
      }
    },
    onError: () => {
      // Fail-soft (D-09): a dispatch error never blocks the canvas; the dealer
      // fills by hand. Clear the reading state so no spinner sticks.
      setIsReading(false);
      setStatus('error');
    },
  });

  const trigger = useCallback(
    (boatId: string) => {
      if (!boatId) return;
      if (!canGenerate) {
        setIsReading(false);
        setStatus(AI_PERMISSION_DENIED_STATUS);
        return;
      }
      setJobId(boatId);
      setIsReading(true); // optimistic — flip off on fail-soft skip/error
      mutation.mutate(boatId);
    },
    [mutation, canGenerate],
  );

  // CR-03: the success-path clear. Wired to useFactoryPacketFill's terminal
  // `done` event (via LiveFillCanvas) so the hero's "now reading…" transition
  // ends when extraction completes — on either the streaming-partial path or
  // the terminal-done refetch path. Stable identity so the fill hook's channel
  // subscription does not re-bind.
  const markReadingComplete = useCallback(() => {
    setIsReading(false);
  }, []);

  return { trigger, jobId, isReading, markReadingComplete, status };
}
